import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { ColumnDef } from '@tanstack/react-table'
import { ReactNode, useState } from 'react'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'
import { Toggle } from 'app/components/Toggle'
import { Tooltip } from 'app/components/Tooltip'
import type { AIAgentConfigurationType } from 'app/types/entities'
import type { AppointmentTypeType } from 'app/types/entities'
import { OptionType } from 'types/OptionType'

export const appointmentTypesSchedulerColumns = (
  onChangeActiveClick: (appointmentTypeId: string, status: boolean) => Promise<void>,
  onEditAppointmentTypeClick: (appointmentType: AppointmentTypeType) => Promise<void>,
): ColumnDef<AppointmentTypeType, any>[] => [
  {
    header: `Name`,
    id: `Name`,
    accessorFn: (row) => row.displayName,
    meta: {
      ControlledComponent: (row, column) => {
        const [tooltipContent, setTooltipContent] = useState<ReactNode>(undefined)
        return (
          <div className="flex items-center justify-between px-2">
            <Tooltip
              className="cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap"
              placement="bottom"
              message={tooltipContent}
              onMouseOver={(event) => {
                if (
                  event.currentTarget instanceof HTMLElement &&
                  event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                ) {
                  setTooltipContent(row?.displayName)
                } else {
                  setTooltipContent(undefined)
                }
              }}
            >
              <div className="flex flex-col gap-1">{row?.displayName}</div>
            </Tooltip>

            <button
              onClick={(e) => onEditAppointmentTypeClick(row!)}
              className="mr-2 hidden h-8 items-center rounded-lg border border-strong bg-surface-subtle p-2 text-ui-xs text-strong group-hover:flex"
            >
              <p>Edit</p>
              <ChevronRightIcon height={16} width={16} />
            </button>
          </div>
        )
      },
      defaultVisible: true,
      pinned: true,
      sortKey: `displayName.keyword`,
    },
    enableHiding: false,
  },
  {
    header: `Display Name`,
    id: `Display Name`,
    accessorFn: (row) => row.alias,
    meta: {
      // group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
  {
    header: `Patient Type`,
    id: `Patient Type`,
    accessorFn: (row) => row.patientType,
    meta: {
      // group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
  {
    header: `Bookable Online`,
    id: `Bookable Online`,
    accessorFn: (row) => row.activeScheduler,
    cell: (props) => (
      <Toggle
        defaultValue={props.getValue()}
        handleToggle={(val) => onChangeActiveClick(props.row.original.id!, val)}
      />
    ),
    meta: {
      // group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
    enableSorting: false,
  },
  {
    header: `Duration`,
    id: `Duration`,
    accessorFn: (row) => row.appointmentLength,
    cell: (props) => <span>{props.getValue()} mins</span>,
    meta: {
      // group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
  {
    header: `Procedure Codes`,
    id: `Procedure Codes`,
    accessorFn: (row) => row.procedureCodes?.join(`, `),
    meta: {
      // group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
]

const options = [
  { name: `General`, id: `general`, value: `general` },
  { name: `Emergency`, id: `emergency`, value: `emergency` },
  { name: `Consultation`, id: `consultation`, value: `consultation` },
  { name: `Limited Exam`, id: `limited exam`, value: `limited exam` },
]

export function getCategoriesForAppointmentType(
  agentConfig: AIAgentConfigurationType | undefined | null,
  appointmentTypeId: string,
): string[] {
  if (!agentConfig) return []
  const categories: string[] = []
  if (agentConfig.generalApptId === appointmentTypeId) categories.push(`general`)
  if (agentConfig.emergencyApptId === appointmentTypeId) categories.push(`emergency`)
  if (agentConfig.consultApptId === appointmentTypeId) categories.push(`consultation`)
  if (agentConfig.limitedExamApptId === appointmentTypeId) categories.push(`limited exam`)
  return categories
}

/** @deprecated Use getCategoriesForAppointmentType instead */
export function getCategoryForAppointmentType(
  agentConfig: AIAgentConfigurationType,
  appointmentTypeId: string,
): string | undefined {
  return getCategoriesForAppointmentType(agentConfig, appointmentTypeId)[0]
}

export function isMappedToCategory(
  agentConfig: AIAgentConfigurationType | undefined | null,
  appointmentTypeId: string,
): boolean {
  return getCategoriesForAppointmentType(agentConfig, appointmentTypeId).length > 0
}

export const appointmentTypesAgentColumns = (
  onChangeActiveClick: (appointmentTypeId: string, status: boolean) => Promise<void>,
  onAppointmentCategoryChange: (appointmentTypeId: string, categories: string[]) => void,
  agentConfig: AIAgentConfigurationType | undefined,
  onEditAppointmentTypeClick?: (appointmentType: AppointmentTypeType) => Promise<void>,
): ColumnDef<AppointmentTypeType, any>[] => [
  {
    header: `Name`,
    id: `Name`,
    accessorFn: (row) => row.displayName,
    meta: {
      ControlledComponent: (row, column) => {
        const [tooltipContent, setTooltipContent] = useState<ReactNode>(undefined)
        return (
          <div className="flex items-center justify-between px-2">
            <Tooltip
              className="cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap"
              placement="bottom"
              message={tooltipContent}
              onMouseOver={(event) => {
                if (
                  event.currentTarget instanceof HTMLElement &&
                  event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                ) {
                  setTooltipContent(row?.displayName)
                } else {
                  setTooltipContent(undefined)
                }
              }}
            >
              <div className="flex flex-col gap-1">{row?.displayName}</div>
            </Tooltip>

            {onEditAppointmentTypeClick && (
              <button
                onClick={(e) => onEditAppointmentTypeClick(row!)}
                className="mr-2 hidden h-8 items-center rounded-lg border border-strong bg-surface-subtle p-2 text-ui-xs text-strong group-hover:flex"
              >
                <p>Edit</p>
                <ChevronRightIcon height={16} width={16} />
              </button>
            )}
          </div>
        )
      },
      defaultVisible: true,
      pinned: true,
      sortKey: `displayName.keyword`,
    },
    enableHiding: false,
  },
  {
    header: `Appointment Category`,
    id: `Appointment Category`,
    meta: {
      ControlledComponent: (row, column) => {
        const selected = row?.id ? getCategoriesForAppointmentType(agentConfig, row.id) : []
        const selectedOptions = options.filter((o) => selected.includes(o.id))
        return (
          <ComboMultiSelect
            name={`category-${row?.id}`}
            options={options}
            values={selectedOptions}
            onChange={(vals) =>
              onAppointmentCategoryChange(
                row!.id!,
                vals.map((v) => v.value),
              )
            }
            className="px-2"
            inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
            placeholder=""
          />
        )
      },
      defaultVisible: true,
      pinned: true,
      sortKey: `displayName.keyword`,
    },
    enableHiding: false,
  },
  {
    header: `Available to Agent`,
    id: `Available to Agent`,
    accessorFn: (row) => row.activeAgent,
    cell: (props) => {
      const isMapped = props.row.original.id ? isMappedToCategory(agentConfig, props.row.original.id) : false
      return (
        <Toggle
          defaultValue={props.getValue()}
          handleToggle={isMapped ? (val) => onChangeActiveClick(props.row.original.id!, val) : undefined}
        />
      )
    },
    meta: {
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
    enableSorting: false,
  },
  {
    header: `Duration`,
    id: `Duration`,
    accessorFn: (row) => row.appointmentLength,
    cell: (props) => <span>{props.getValue()} mins</span>,
    meta: {
      // group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
  {
    header: `Procedure Codes`,
    id: `Procedure Codes`,
    accessorFn: (row) => row.procedureCodes?.join(`, `),
    meta: {
      // group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
]
